import { type FormEventHandler, useCallback, useId, useMemo } from "react";
import type { MapStyle } from "../alk-maps";
import { inject } from "../ng";
import { Popover, PopoverButton, PopoverPanel } from "../popover/popover";
import { stopArrowKeyPropagation } from "../utils";
import classes from "./map-nav.module.css";
import { popoverProps } from "./utils";

export const mapStyles = (
	[
		["Road Map", "transportation", "/img/mapstyle-default.svg"],
		["Satellite Map", "satellite", "/img/mapstyle-satellite-roads.svg"],
		["Satellite Only", "satellite_only", "/img/mapstyle-satellite-only.svg"],
		["Terrain Map", "terrain", "/img/mapstyle-terrain.svg"],
		["Light Data Map", "datalight", "/img/mapstyle-monochrome.svg"],
		["Dark Data Map", "datadark", "/img/mapstyle-contemporary.svg"],
	] as const
).map(([name, id, icon]) => ({ name, id, icon }));

export type UiMapStyle = (typeof mapStyles)[0]["id"];

export function MapStyles() {
	const id = useId();

	const setMapStyle = useMemo(() => {
		const map = inject("mapService").getMap();
		return (style: UiMapStyle) => {
			const satelliteOnly = style === "satellite_only";
			const mapstyle: MapStyle = satelliteOnly ? "satellite" : style;
			map.baseLayer.satelliteOnly = satelliteOnly;
			map.baseLayer.changeStyle(mapstyle);
		};
	}, []);

	const onInput = useCallback<FormEventHandler>(
		(event) => {
			const style = (event.target as HTMLInputElement).value as UiMapStyle;
			setMapStyle(style);
		},
		[setMapStyle],
	);

	return (
		<Popover {...popoverProps}>
			<PopoverButton title="Map Styles" className={classes.button}>
				<svg aria-hidden="true" className={classes.icon} viewBox="0 0 24 24">
					<path d="M12 2a10 10 0 1 0 10 10A10.03 10.03 0 0 0 12 2m0 18.3a8.2 8.2 0 0 1-2.8-.5 3.1 3.1 0 0 0 .8-1.3c.1-.3-.2-1-.2-1.1a9 9 0 0 0-1-1.3c-.2-.1 0-.9 0-1.4s-1.1-.8-1.5-1c-.4-.3-.6.8-1 .6-.5-.2-.4-.9-.5-1.5 0-.4-1.1-.5-1.7-.6a7.99 7.99 0 0 1 5.5-7.6l-.1.1c-.5.4-1 0-.8 1.2.1 1.1-.1 1.7.8 1.8a5.4 5.4 0 0 0 1.8 0c.6 0 .6 1.5.7 1.6a5.08 5.08 0 0 0 3.1 2.8c.5.1-.5 1.1-1.2 1.4s-1.3.4-1.4 1.1 1 .3 1.3.4 1.5.6 1.9-.1c.5-.7 2.5-.4 2.3-1.2-.4-1.6 2.3-2.4 1.5-3.4a6.8 6.8 0 0 1-.2-1.8 7.8 7.8 0 0 1 .9 3.7 8.283 8.283 0 0 1-8.2 8.1" />
				</svg>
			</PopoverButton>
			<PopoverPanel>
				<div className={classes.heading}>Map Styles</div>
				<div
					className={classes.listing}
					onKeyDown={stopArrowKeyPropagation}
					onInput={onInput}
				>
					{mapStyles.map((style, index) => (
						<label key={style.id} className={classes.button}>
							<input
								className={classes.hiddenInput}
								type="radio"
								value={style.id}
								name={id}
								defaultChecked={index === 0}
							/>
							<img
								aria-label={style.name}
								src={style.icon}
								width="24"
								height="24"
							/>
							{style.name}
						</label>
					))}
				</div>
			</PopoverPanel>
		</Popover>
	);
}
